/**
 * 2026-10-21 (Matt, card 21575): the card's Cancel asked nothing. A patient who
 * had confirmed by text was cancelled with one click, nothing reached her, and
 * she came in. Cancel now always asks, and says what the patient knows.
 *
 * `appt` = a row of GET /crm/patients/{id}/appointments (patient_texted,
 * patient_confirmed_at, cancel_text_on); `isPast` = the visit's wall clock has
 * passed (lib/wallClock isWallClockPast). Returns the confirm text.
 *
 * 2026-10-23 (Matt's review of #51): the prompt can't know everything ahead (a
 * rebooked patient gets no cancel text; nor does a cancel Raintree doesn't
 * confirm), so it says "unless they're already rebooked", and after the cancel
 * `cancelResultText` turns the answer's `patient_text` word into what actually
 * happened.
 */
const fmtDay = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString([], { month: "short", day: "numeric" });
};

export function cancelPromptText(appt, isPast) {
  if (isPast) {
    return "Cancel this past appointment?\n\nNo text goes to the patient for a visit that has passed.";
  }
  const tell = appt && appt.cancel_text_on
    ? "Cancelling texts them that it was canceled (unless they're already rebooked). Please also make sure they know."
    : "Please make sure they know it's canceled - no text goes out for it.";
  if (appt && appt.patient_confirmed_at) {
    const day = fmtDay(appt.patient_confirmed_at);
    return `This patient confirmed this visit by text${day ? ` on ${day}` : ""} and is expecting to come.\n\n${tell}\n\nCancel the appointment?`;
  }
  if (appt && appt.patient_texted) {
    return `This patient was texted about this visit.\n\n${tell}\n\nCancel the appointment?`;
  }
  return "Cancel this appointment?";
}

const LET_THEM_KNOW = "please make sure the patient knows.";

/**
 * After the card's Cancel: the answer's `patient_text` word (services/post_cancel.after_cancel)
 * -> { text, warn } for a toast, or null when the answer has no word (an older API).
 * warn = staff should tell the patient themselves.
 */
export function cancelResultText(word) {
  if (!word) return null;
  switch (word) {
    case "enqueued":
      return { text: "Cancelled. Cancel text queued for the patient.", warn: false };
    case "already_enqueued":
      return { text: "Cancelled. The cancel text was already queued.", warn: false };
    case "rebooked":
      return { text: "Cancelled. No text: the patient is already rebooked.", warn: false };
    case "visit_passed":
      return { text: "Cancelled. No text: the visit had passed.", warn: false };
    case "not_texted":
      return { text: `Cancelled. No text: the patient wasn't texted about this visit - ${LET_THEM_KNOW}`, warn: true };
    case "template_not_enabled":
      return { text: `Cancelled. No text: cancel texts are off - ${LET_THEM_KNOW}`, warn: true };
    case "raintree_refused":
      return { text: `Cancelled here, but Raintree didn't confirm it. No text went out - ${LET_THEM_KNOW}`, warn: true };
    default:
      return { text: `Cancelled. No cancel text went out - ${LET_THEM_KNOW}`, warn: true };
  }
}
